/** @format */

import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Paper,
  Fade,
  Snackbar,
  Alert,
} from "@mui/material";
import { Box } from "./MuiCompat";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getMainGradient } from "./utils";
import { trackEvent, EventNames } from "../utils/analytics";

const RootContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  minHeight: "100vh",
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(8),
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: theme.spacing(10),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  marginBottom: theme.spacing(3),
  textAlign: "center",
  fontFamily: "'Poppins', sans-serif",
  fontSize: "3rem",
  [theme.breakpoints.down("md")]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 300,
  fontSize: "1.3rem",
  marginBottom: theme.spacing(6),
  textAlign: "center",
  fontFamily: "'Poppins', sans-serif",
  maxWidth: "600px",
  lineHeight: 1.6,
}));

const StyledForm = styled("form")(({ theme }) => ({
  width: "100%",
  maxWidth: "600px",
  marginTop: theme.spacing(4),
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  border: "1px solid rgba(255, 255, 255, 0.2)",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.5)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "white",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "white",
  },
  "& .MuiInputBase-input": {
    color: "white",
  },
}));

const CustomReasonTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.5)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "white",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "white",
  },
  "& .MuiInputBase-input": {
    color: "white",
  },
}));

const SurveySection = styled(Box)(({ theme }) => ({
  //   marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const SurveyTitle = styled(FormLabel)(({ theme }) => ({
  fontWeight: 400,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(2),
  color: "rgba(255, 255, 255, 0.9)",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  "& .MuiFormControlLabel-root": {
    marginBottom: theme.spacing(1),
  },
  "& .MuiRadio-root": {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-checked": {
      color: "white",
    },
  },
  "& .MuiFormControlLabel-label": {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: "0.95rem",
    fontFamily: "'Poppins', sans-serif",
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  width: "100%",
  borderRadius: "25px",
  backgroundColor: "rgba(220, 53, 69, 0.9)",
  boxShadow: "none",
  color: "#fff",
  fontSize: "1.1rem",
  fontWeight: 500,
  height: "50px",
  textTransform: "none",
  transition: "all 0.3s ease-in",
  fontFamily: "'Poppins', sans-serif",
  position: "relative",
  "&:hover": {
    backgroundColor: "rgba(220, 53, 69, 1)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(220, 53, 69, 0.3)",
  },
  "&:not([disabled])": {
    "&::after": {
      content: '""',
      position: "absolute",
      width: "0%",
      height: "2px",
      bottom: "8px",
      left: "50%",
      backgroundColor: "#fff",
      transform: "translateX(-50%)",
      transition: "width 0.3s ease-in",
    },
    "&:hover::after": {
      width: "60%",
    },
  },
}));

const SuccessContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: 1,
  minHeight: "50vh",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: "700px",
  padding: theme.spacing(6),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: "80px",
  margin: theme.spacing(2, 0, 4),
  color: "#4caf50",
}));

const SuccessTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: "2.5rem",
  marginBottom: theme.spacing(3),
  textAlign: "center",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const SuccessMessage = styled(Typography)(({ theme }) => ({
  fontWeight: 300,
  fontSize: "1.2rem",
  marginBottom: theme.spacing(4),
  textAlign: "center",
  fontFamily: "'Poppins', sans-serif",
  maxWidth: "600px",
  lineHeight: 1.6,
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: "25px",
  background: getMainGradient(),
  color: "#ffffff",
  textTransform: "none",
  padding: "12px 32px",
  fontSize: "1rem",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const unsubscribeReasons = [
  "I no longer find the content relevant",
  "I receive too many emails",
  "I never signed up for this mailing list",
  "I am no longer interested",
  "Other",
];

const Unsubscribe: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get("email") || "";

  const [formData, setFormData] = useState({
    email: emailFromParams,
    reason: "",
    customReason: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Update email if it changes in URL params
  useEffect(() => {
    const newEmail = searchParams.get("email") || "";
    if (newEmail !== formData.email) {
      setFormData((prev) => ({
        ...prev,
        email: newEmail,
      }));
    }
  }, [searchParams]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      reason: e.target.value,
    }));
  };

  const isFormValid = () => {
    const isValidEmail =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(
        formData.email.trim()
      );
    return formData.email.trim() !== "" && isValidEmail;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid()) {
      setSnackbar({
        open: true,
        message: "Please provide a valid email address.",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Determine the final reason to send
      const finalReason =
        formData.reason === "Other" && formData.customReason.trim()
          ? formData.customReason.trim()
          : formData.reason || "none_selected";

      // Track unsubscribe attempt
      trackEvent(EventNames.BUTTON_CLICK, {
        button_name: "unsubscribe_submit",
        reason: finalReason,
        email_domain: formData.email.split("@")[1] || "",
      });

      const response = await fetch("https://api.eon.health/unsubscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          reason: finalReason,
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setIsUnsubscribed(true);

        // Track successful unsubscribe
        trackEvent("unsubscribe_success", {
          reason: finalReason,
          email_domain: formData.email.split("@")[1] || "",
        });
      } else {
        throw new Error("Failed to unsubscribe");
      }
    } catch (error) {
      console.error("Unsubscribe error:", error);
      setSnackbar({
        open: true,
        message:
          "An error occurred while processing your request. Please try again or contact support.",
        severity: "error",
      });

      // Track unsubscribe error
      trackEvent("unsubscribe_error", {
        error_message: error instanceof Error ? error.message : "Unknown error",
        email_domain: formData.email.split("@")[1] || "",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleBackToHome = () => {
    trackEvent(EventNames.BUTTON_CLICK, {
      button_name: "back_to_home_from_unsubscribe",
    });
    window.location.href = "/";
  };

  if (isUnsubscribed) {
    return (
      <RootContainer>
        <Container maxWidth="lg">
          <ContentContainer>
            <Fade in={isUnsubscribed} timeout={800}>
              <SuccessContainer>
                <SuccessIcon />
                <SuccessTitle variant="h2">
                  You've Been Unsubscribed
                </SuccessTitle>
                <SuccessMessage variant="body1">
                  We're sorry to see you go! You have been successfully removed
                  from our mailing list and will no longer receive emails from
                  us.
                </SuccessMessage>
                <SuccessMessage variant="body2">
                  If this was a mistake or you'd like to resubscribe in the
                  future, feel free to reach us at contact@eon.health.
                </SuccessMessage>
                <BackButton
                  onClick={handleBackToHome}
                  variant="contained"
                  size="large"
                >
                  Back to Home
                </BackButton>
              </SuccessContainer>
            </Fade>
          </ContentContainer>
        </Container>
      </RootContainer>
    );
  }

  return (
    <RootContainer>
      <Container maxWidth="lg">
        <ContentContainer>
          <StyledTitle variant="h1">Unsubscribe</StyledTitle>
          <StyledSubtitle variant="body1">
            We're sorry to see you go. To complete your unsubscription, please
            confirm your email address below and help us understand why you're
            leaving.
          </StyledSubtitle>

          <FormPaper elevation={0}>
            <StyledForm onSubmit={handleSubmit}>
              <StyledTextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isSubmitting}
                required
              />

              <SurveySection>
                <SurveyTitle>Why are you unsubscribing? (Optional)</SurveyTitle>
                <StyledRadioGroup
                  name="reason"
                  value={formData.reason}
                  onChange={handleReasonChange}
                >
                  {unsubscribeReasons.map((reason) => (
                    <FormControlLabel
                      key={reason}
                      value={reason}
                      control={<Radio />}
                      label={reason}
                      disabled={isSubmitting}
                    />
                  ))}
                </StyledRadioGroup>

                {formData.reason === "Other" && (
                  <CustomReasonTextField
                    fullWidth
                    label="Please specify your reason (optional)"
                    name="customReason"
                    value={formData.customReason}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    multiline
                    rows={3}
                    placeholder="Tell us more about why you're unsubscribing..."
                  />
                )}
              </SurveySection>

              <SubmitButton
                type="submit"
                variant="contained"
                disabled={isSubmitting || !isFormValid()}
              >
                {isSubmitting ? "Processing..." : "Unsubscribe"}
              </SubmitButton>
            </StyledForm>
          </FormPaper>
        </ContentContainer>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </RootContainer>
  );
};

export default Unsubscribe;
